import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import {
    IndianRupee,
    TrendingUp,
    CreditCard,
    AlertCircle,
    CheckCircle,
    XCircle,
    Clock,
    RefreshCw,
    Search,
    ArrowRight,
    Settings
} from "lucide-react";
import apiService from "../services/api";
import { toast } from "react-hot-toast";

function Financial() {
    const [activeTab, setActiveTab] = useState("overview"); // overview, payouts
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        totalBookingValue: 0,
        totalPlatformRevenue: 0,
        totalPayoutsProcessed: 0,
        pendingPayoutRequestsCount: 0,
        totalPendingPanditBalance: 0
    });
    const [globalCommission, setGlobalCommission] = useState(20);
    const [payouts, setPayouts] = useState([]);
    const [payoutStats, setPayoutStats] = useState({ requests: [], total: 0 });
    const [payoutAction, setPayoutAction] = useState(null); // { id, type: 'APPROVE' | 'REJECT' }
    const [actionNote, setActionNote] = useState("");
    const [transactionRef, setTransactionRef] = useState("");
    const [actionLoading, setActionLoading] = useState(false);
    const [commissionLoading, setCommissionLoading] = useState(false);

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        setLoading(true);
        await Promise.all([
            fetchStats(),
            fetchGlobalCommission(),
            fetchPayouts()
        ]);
        setLoading(false);
    };

    const fetchStats = async () => {
        try {
            const response = await apiService.getAdminFinancialStats();
            if (response.success) {
                setStats(response.data);
            }
        } catch (error) {
            console.error("Error fetching stats:", error);
        }
    };

    const fetchGlobalCommission = async () => {
        try {
            const response = await apiService.getGlobalCommission();
            if (response.success) {
                setGlobalCommission(response.data.percentage);
            }
        } catch (error) {
            console.error("Error fetching commission:", error);
        }
    };

    const fetchPayouts = async () => {
        try {
            // Fetch pending payouts by default
            const response = await apiService.getPayoutRequests("status=REQUESTED");
            if (response.success) {
                setPayouts(response.data.requests);
                setPayoutStats(response.data);
            }
        } catch (error) {
            console.error("Error fetching payouts:", error);
        }
    };

    const handleUpdateCommission = async () => {
        try {
            setCommissionLoading(true);
            await apiService.updateGlobalCommission(globalCommission);
            toast.success("Global commission updated successfully");
        } catch (error) {
            toast.error("Failed to update commission");
        } finally {
            setCommissionLoading(false);
        }
    };

    const executePayoutAction = async () => {
        if (!payoutAction) return;

        try {
            setActionLoading(true);
            await apiService.processPayoutRequest(
                payoutAction.id,
                payoutAction.type,
                actionNote,
                transactionRef
            );
            toast.success(`Payout ${payoutAction.type === 'APPROVE' ? 'approved' : 'rejected'} successfully`);
            setPayoutAction(null);
            setActionNote("");
            setTransactionRef("");
            fetchPayouts(); // Refresh list
            fetchStats(); // Refresh stats
        } catch (error) {
            toast.error("Failed to process payout request");
        } finally {
            setActionLoading(false);
        }
    };



    if (loading && !stats.totalBookingValue) {
        return (
            <div className="flex items-center justify-center h-64">
                <RefreshCw className="w-8 h-8 animate-spin text-orange-600" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                        Financial Management
                    </h1>
                    <p className="mt-2 text-gray-600">
                        Monitor revenue, commissions, and process payouts
                    </p>
                </div>

                <div className="mt-4 sm:mt-0">
                    <div className="bg-white p-1 rounded-lg border border-gray-200 inline-flex">
                        <button
                            onClick={() => setActiveTab('overview')}
                            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === 'overview'
                                ? 'bg-orange-100 text-orange-700'
                                : 'text-gray-600 hover:bg-gray-50'
                                }`}
                        >
                            Overview
                        </button>
                        <button
                            onClick={() => setActiveTab('payouts')}
                            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === 'payouts'
                                ? 'bg-orange-100 text-orange-700'
                                : 'text-gray-600 hover:bg-gray-50'
                                }`}
                        >
                            Payout Requests
                            {stats.pendingPayoutRequestsCount > 0 && (
                                <span className="ml-2 bg-red-100 text-red-600 px-2 py-0.5 rounded-full text-xs">
                                    {stats.pendingPayoutRequestsCount}
                                </span>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {activeTab === 'overview' && (
                <div className="space-y-6">
                    {/* Stats Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                            <div className="flex items-center">
                                <div className="p-2 bg-green-100 rounded-lg">
                                    <IndianRupee className="w-6 h-6 text-green-600" />
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-600">Total Platform Revenue</p>
                                    <p className="text-2xl font-bold text-gray-900">₹{stats.totalPlatformRevenue.toFixed(2)}</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                            <div className="flex items-center">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                    <TrendingUp className="w-6 h-6 text-blue-600" />
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-600">Total Booking Value</p>
                                    <p className="text-2xl font-bold text-gray-900">₹{stats.totalBookingValue.toFixed(2)}</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                            <div className="flex items-center">
                                <div className="p-2 bg-yellow-100 rounded-lg">
                                    <Clock className="w-6 h-6 text-yellow-600" />
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-600">Pending Payouts</p>
                                    <p className="text-2xl font-bold text-gray-900">{stats.pendingPayoutRequestsCount}</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                            <div className="flex items-center">
                                <div className="p-2 bg-purple-100 rounded-lg">
                                    <CreditCard className="w-6 h-6 text-purple-600" />
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-600">Total Processed Payouts</p>
                                    <p className="text-2xl font-bold text-gray-900">₹{stats.totalPayoutsProcessed.toFixed(2)}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Commission Settings */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 max-w-md">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            <Settings className="w-5 h-5 mr-2" />
                            Global Commission Settings
                        </h2>
                        <div className="flex items-end space-x-4">
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Default Commission Percentage (%)
                                </label>
                                <input
                                    type="number"
                                    value={globalCommission}
                                    onChange={(e) => setGlobalCommission(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                                    min="0"
                                    max="100"
                                />
                            </div>
                            <button
                                onClick={handleUpdateCommission}
                                disabled={commissionLoading}
                                className="px-4 py-2 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                            >
                                {commissionLoading && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
                                {commissionLoading ? 'Updating...' : 'Update'}
                            </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                            This applies to all new bookings where a pandit-specific commission is not set.
                        </p>
                    </div>
                </div>
            )}

            {activeTab === 'payouts' && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                        <h3 className="font-semibold text-gray-800">Pending Requests</h3>
                        <button
                            onClick={fetchPayouts}
                            className="text-sm text-orange-600 hover:text-orange-700 flex items-center"
                        >
                            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                        </button>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pandit</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bank Details</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requested At</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {payouts.length > 0 ? (
                                    payouts.map((request) => (
                                        <tr key={request.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="h-8 w-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-bold mr-3">
                                                        {request.pandit?.profile?.name?.[0] || 'P'}
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900">{request.pandit?.profile?.name || 'Unknown'}</div>
                                                        <div className="text-sm text-gray-500">{request.pandit?.email}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-bold text-gray-900">₹{request.amount}</div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-xs text-gray-600">
                                                    <p><span className="font-medium">Bank:</span> {request.bankDetailsSnapshot?.bankName}</p>
                                                    <p><span className="font-medium">Acc:</span> {request.bankDetailsSnapshot?.accountNumber}</p>
                                                    <p><span className="font-medium">IFSC:</span> {request.bankDetailsSnapshot?.ifscCode}</p>
                                                    <p><span className="font-medium">Holder:</span> {request.bankDetailsSnapshot?.accountHolderName}</p>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {format(new Date(request.createdAt), "MMM dd, yyyy HH:mm")}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                                                <button
                                                    onClick={() => setPayoutAction({ id: request.id, type: 'APPROVE' })}
                                                    className="text-green-600 hover:text-green-900 bg-green-50 px-3 py-1 rounded-full text-xs font-semibold"
                                                >
                                                    Approve
                                                </button>
                                                <button
                                                    onClick={() => setPayoutAction({ id: request.id, type: 'REJECT' })}
                                                    className="text-red-600 hover:text-red-900 bg-red-50 px-3 py-1 rounded-full text-xs font-semibold"
                                                >
                                                    Reject
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                                            No pending payout requests
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Modal Logic Inlined */}
            {payoutAction && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl max-w-md w-full p-6">
                        <h3 className="text-xl font-semibold mb-4">
                            {payoutAction.type === 'APPROVE' ? 'Approve Payout' : 'Reject Payout'}
                        </h3>

                        <div className="space-y-4">
                            {payoutAction.type === 'APPROVE' && (
                                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 text-sm text-blue-700 mb-4">
                                    <p className="font-semibold flex items-center">
                                        <AlertCircle className="w-4 h-4 mr-1" />
                                        Razorpay Payout
                                    </p>
                                    <p className="mt-1">
                                        Approving this request will <strong>immediately initiate a fund transfer</strong> to the pandit's bank account via Razorpay.
                                    </p>
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {payoutAction.type === 'APPROVE' ? 'Admin Notes (Optional)' : 'Rejection Reason (Required)'}
                                </label>
                                <textarea
                                    value={actionNote}
                                    onChange={(e) => setActionNote(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                                    rows="3"
                                    placeholder={payoutAction.type === 'APPROVE' ? "Notes..." : "Reason for rejection..."}
                                    autoFocus
                                />
                            </div>

                            <div className="flex space-x-3 pt-4">
                                <button
                                    onClick={() => setPayoutAction(null)}
                                    disabled={actionLoading}
                                    className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={executePayoutAction}
                                    disabled={actionLoading}
                                    className={`flex-1 px-4 py-2 text-white rounded-lg flex items-center justify-center ${payoutAction.type === 'APPROVE'
                                        ? 'bg-green-600 hover:bg-green-700'
                                        : 'bg-red-600 hover:bg-red-700'
                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                >
                                    {actionLoading ? (
                                        <>
                                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            Confirm {payoutAction.type === 'APPROVE' ? 'Approval' : 'Rejection'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Financial;
